import {useState} from 'react'
import {PageHeader} from '../components/ui/page-header'
import {DataTable} from '../components/ui/data-table'
import {
    LVS_COLORS,
    LVS_COLOR_KEYS,
    isDarkColor,
    type LvsColor,
    formatCmykDisplay
} from '../lib/lvs-colors'

const COLOR_ENTRIES: readonly LvsColor[] = LVS_COLOR_KEYS.map(k => LVS_COLORS[k])

interface SwatchProps {
    color: LvsColor
    copied: boolean
    onCopy: (hex: string) => void
}

function Swatch({color, copied, onCopy}: SwatchProps) {
    const dark = isDarkColor(color.hex)
    const fg = dark ? LVS_COLORS.white.hex : LVS_COLORS.black.hex

    return (
        <button
            type='button'
            onClick={() => onCopy(color.hex)}
            className='group relative flex h-40 flex-col items-center justify-center overflow-hidden border border-foreground/10 transition-colors hover:border-foreground/30 focus:outline-none focus:border-primary focus:outline-2 focus:outline-primary/40'
            style={{backgroundColor: color.hex, color: fg}}
            aria-label={`Kopēt HEX ${color.hex}`}
        >
            <div className='flex flex-col items-center justify-center opacity-100 transition-opacity duration-150 group-hover:opacity-0 group-focus:opacity-0'>
                <span className='text-2xl font-bold tracking-tight'>{color.label}</span>
                <span className='mt-1 font-mono text-xs opacity-70'>{color.ral}</span>
            </div>

            <div className='absolute inset-0 flex flex-col items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100'>
                <span className='font-mono text-xl font-bold'>{color.hex}</span>
                <span className='font-mono text-[11px] mt-1 opacity-80'>
                    CMYK {formatCmykDisplay(color.cmyk100)}
                </span>
                <span className='font-mono text-[10px] tracking-[0.2em] uppercase opacity-70 mt-2'>
                    {copied ? 'Nokopēts ✓' : 'Klikšķis kopē'}
                </span>
            </div>
        </button>
    )
}

export function ColorsPage() {
    const [copiedKey, setCopiedKey] = useState<string | null>(null)

    const handleCopy = (hex: string) => {
        navigator.clipboard?.writeText(hex).catch(() => {})
        setCopiedKey(hex)
        window.setTimeout(() => {
            setCopiedKey(prev => (prev === hex ? null : prev))
        }, 1200)
    }

    return (
        <div className='space-y-8'>
            {/* ── Header ── */}
            <PageHeader
                eyebrow='LVS 77-3:2016 / LVS EN 12899-1'
                title='Krāsu palete'
                subtitle='Ceļa zīmju standarta krāsas — RAL kodi atbilstoši LVS EN 12899-1, HEX vērtības ekrāna attēlojumam sRGB telpā, CMYK iespiedmateriāliem. Noklikšķini paraugu, lai kopētu HEX.'
            />

            {/* ── Swatch grid ── */}
            <div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>
                {COLOR_ENTRIES.map(color => (
                    <Swatch
                        key={color.key}
                        color={color}
                        copied={copiedKey === color.hex}
                        onCopy={handleCopy}
                    />
                ))}
            </div>

            {/* ── Data table ── */}
            <DataTable>
                <DataTable.Head>
                    <DataTable.HeadCell className='w-20'>Paraugs</DataTable.HeadCell>
                    <DataTable.HeadCell>Atslēga</DataTable.HeadCell>
                    <DataTable.HeadCell>Nosaukums</DataTable.HeadCell>
                    <DataTable.HeadCell className='text-primary'>RAL</DataTable.HeadCell>
                    <DataTable.HeadCell className='text-primary'>HEX</DataTable.HeadCell>
                    <DataTable.HeadCell className='text-primary'>CMYK</DataTable.HeadCell>
                    <DataTable.HeadCell>Lietojums</DataTable.HeadCell>
                </DataTable.Head>
                <tbody>
                    {COLOR_ENTRIES.map(color => (
                        <DataTable.Row key={color.key}>
                            <DataTable.Cell>
                                <span
                                    className='inline-block h-6 w-10 border border-foreground/15 align-middle'
                                    style={{backgroundColor: color.hex}}
                                />
                            </DataTable.Cell>
                            <DataTable.Cell className='text-foreground/50'>
                                {color.key}
                            </DataTable.Cell>
                            <DataTable.Cell className='text-foreground font-bold text-sm'>
                                {color.label}
                            </DataTable.Cell>
                            <DataTable.Cell className='text-primary font-medium tabular-nums'>
                                {color.ral}
                            </DataTable.Cell>
                            <DataTable.Cell className='text-primary font-medium tabular-nums'>
                                {color.hex}
                            </DataTable.Cell>
                            <DataTable.Cell className='text-primary font-medium tabular-nums'>
                                {formatCmykDisplay(color.cmyk100)}
                            </DataTable.Cell>
                            <DataTable.Cell className='text-foreground/70'>
                                {color.description}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ))}
                </tbody>
                <DataTable.Foot>
                    <DataTable.Cell
                        colSpan={2}
                        className='text-foreground/40 tracking-widest uppercase'
                    >
                        Kopā
                    </DataTable.Cell>
                    <DataTable.Cell
                        colSpan={5}
                        className='text-foreground/70 font-medium'
                    >
                        {COLOR_ENTRIES.length} krāsas
                    </DataTable.Cell>
                </DataTable.Foot>
            </DataTable>
        </div>
    )
}
